/**
 * Approval-chain authoring API client — G5-BUILD-A.
 *
 * Live contract (measured at e437e94b6, backend
 * work-management/controllers/gate-approval-chain.controller.ts):
 *   POST   /work/gate-definitions/approval-chains            create-or-replace
 *   PUT    /work/gate-definitions/approval-chains/:id/reorder
 *   DELETE /work/gate-definitions/approval-chains/:id
 *   GET    /work/gate-definitions/:gateDefinitionId/approval-chain
 *   GET    /work/gate-definitions/approval-chains/:chainId
 *
 * Three measured constraints shape this module. They are not style choices:
 *
 * 1. `x-workspace-id` is attached AUTOMATICALLY by the shared client
 *    (lib/api.ts request interceptor). Never pass it here. For any `/work/`
 *    URL the client THROWS `WORKSPACE_REQUIRED` before the request leaves the
 *    browser when `useWorkspaceStore.activeWorkspaceId` is unset.
 *
 * 2. Nested step-array validation failures come back as
 *    `{ code: 'VALIDATION_ERROR', message: 'Invalid request' }` — no field, no
 *    step index, no permitted values — because the pipe's exceptionFactory and
 *    buildValidationError do not descend into ValidationError.children.
 *    Top-level field failures DO name the field. So step-level errors are
 *    UNATTRIBUTABLE from the response and must be surfaced as such, never
 *    guessed at. Client-side validation is the only way a user learns which
 *    step is wrong.
 *
 * 3. ENVELOPE-READ-DENIALS-1 / #1090: chain GETs now throw real 403s
 *    (`AUTH_FORBIDDEN`) via `ApiErrorFilter`. A resolved `null` is a genuine
 *    absent chain (`EMPTY`). See `readChainForGate`.
 */
import type { ApprovalType } from './phaseGates.api';

import { request } from '@/lib/api';

// ─── Types ──────────────────────────────────────────────────

/**
 * Roles that may be authored as a required step target.
 *
 * ⚠️ VIEWER IS DELIBERATELY EXCLUDED — VIEWER-STEP-REFUSAL-1 (#1065 / #1091),
 * founder ruling 2026-09-23. A VIEWER-required step enumerates approvers and
 * notifies exactly zero of them (dispatch skips VIEWER). The backend allow-list
 * is PlatformRole minus VIEWER (ADMIN, MEMBER), case-sensitive, enforced by
 * `@IsIn` on the DTO and re-checked in the service.
 *
 * Legacy rows that already store `requiredRole: VIEWER` remain readable
 * (`ApprovalChainStep.requiredRole` stays `string | null`). Do not narrow the
 * read type or blank those steps in the UI — only refuse offering VIEWER for
 * new writes.
 */
export const APPROVAL_STEP_ROLES = ['ADMIN', 'MEMBER'] as const;
export type ApprovalStepRole = (typeof APPROVAL_STEP_ROLES)[number];

/**
 * One authored step. `requiredRole` and `requiredUserId` are each optional on
 * the wire and the DTO does NOT enforce "exactly one" — a step carrying
 * NEITHER passes the ValidationPipe and is rejected only by the service. The
 * caller is responsible for the invariant; see `validateStep`.
 *
 * `requiredRole` is typed as `string` (not only ApprovalStepRole) so a loaded
 * legacy VIEWER step can round-trip through validation messaging without a
 * type lie. New writes are still checked against APPROVAL_STEP_ROLES.
 */
export interface ApprovalStepInput {
  name: string;
  description?: string;
  requiredRole?: string;
  requiredUserId?: string;
  approvalType?: ApprovalType;
  minApprovals?: number;
}

/**
 * Options for a role `<select>`. Authoring offers only APPROVAL_STEP_ROLES;
 * if the current value is a legacy role (e.g. VIEWER), include it so the
 * existing step still RENDERS — backend refuses writes, not reads.
 */
export function roleOptionsForSelect(currentRole?: string | null): string[] {
  const allowed: string[] = [...APPROVAL_STEP_ROLES];
  if (currentRole && !allowed.includes(currentRole)) {
    return [...allowed, currentRole];
  }
  return allowed;
}

export interface CreateApprovalChainInput {
  gateDefinitionId: string;
  name: string;
  description?: string;
  steps: ApprovalStepInput[];
}

/** Seeded step title. Not a person's name and not the role itself. */
export const SEEDED_APPROVAL_STEP_NAME = 'Admin approval';

/**
 * Named-person steps read "Approval by <name>".
 * Role steps read the role (ADMIN), not the seeded step title.
 */
export function approvalStepLabel(input: {
  requiredUserId?: string | null;
  requiredRole?: string | null;
  personName?: string | null;
  storedName?: string | null;
}): string {
  const stored = input.storedName?.trim() ?? '';
  const person = input.personName?.trim() ?? '';
  if (input.requiredUserId) {
    if (person) return `Approval by ${person}`;
    if (stored.startsWith('Approval by ') && stored !== 'Approval by a specific person') {
      return stored;
    }
    return 'Approval by a specific person';
  }
  const role = input.requiredRole?.trim();
  if (role) return role;
  if (stored && stored.toLowerCase() !== SEEDED_APPROVAL_STEP_NAME.toLowerCase()) {
    return stored;
  }
  return role || 'ADMIN';
}

export interface ApprovalChainStep {
  id: string;
  stepOrder: number;
  name: string;
  description: string | null;
  requiredRole: string | null;
  requiredUserId: string | null;
  approvalType: ApprovalType;
  minApprovals: number;
}

export interface ApprovalChain {
  id: string;
  gateDefinitionId: string;
  name: string;
  description: string | null;
  isActive: boolean;
  steps: ApprovalChainStep[];
}

// ─── Client-side validation ─────────────────────────────────

/**
 * Per-step invariants the SERVER will reject but the ValidationPipe will not
 * attribute. Validating here is the only way a user learns which step failed
 * (constraint 2 above).
 */
export interface StepValidationError {
  stepIndex: number;
  field: 'target' | 'name' | 'requiredRole' | 'requiredUserId';
  message: string;
}

const UUID_RE =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export function validateStep(
  step: ApprovalStepInput,
  stepIndex: number,
): StepValidationError[] {
  const errors: StepValidationError[] = [];

  if (!step.name || step.name.trim() === '') {
    errors.push({ stepIndex, field: 'name', message: 'Step name is required.' });
  }

  const hasRole = Boolean(step.requiredRole);
  const hasUser = Boolean(step.requiredUserId);

  // The DTO accepts neither and accepts both. The service rejects neither, and
  // "both" resolves in an order the UI should not rely on. Require exactly one.
  if (!hasRole && !hasUser) {
    errors.push({
      stepIndex,
      field: 'target',
      message: 'Choose who approves this step: a person or a role.',
    });
  }
  if (hasRole && hasUser) {
    errors.push({
      stepIndex,
      field: 'target',
      message:
        'Choose only one approver target for this step: a person or a role, not both.',
    });
  }

  if (hasRole && !(APPROVAL_STEP_ROLES as readonly string[]).includes(step.requiredRole!)) {
    errors.push({
      stepIndex,
      field: 'requiredRole',
      message: `Role must be one of ${APPROVAL_STEP_ROLES.join(', ')}.`,
    });
  }

  if (hasUser && !UUID_RE.test(step.requiredUserId!)) {
    errors.push({
      stepIndex,
      field: 'requiredUserId',
      message: 'Choose an approver from the list.',
    });
  }

  return errors;
}

export function validateChainInput(
  input: CreateApprovalChainInput,
): StepValidationError[] {
  return input.steps.flatMap((step, i) => validateStep(step, i));
}

// ─── Failure classification ─────────────────────────────────

/**
 * Discriminated by a STRING TAG, never a boolean — see
 * .claude/rules/backend-typescript.md. The same hazard applies to any union
 * consumed under a loose tsconfig.
 */
export type ChainWriteFailure =
  /** The shared client refused before sending: no active workspace selected. */
  | { outcome: 'WORKSPACE_MISSING'; message: string }
  /**
   * 403. Measured: elevation requires a REAL, active membership row — an org
   * admin with no membership in this workspace is NOT elevated and is denied.
   * This is a membership problem, not a generic permission error.
   */
  | { outcome: 'NOT_A_MEMBER'; message: string }
  /** 409 GATE_CHAIN_LOCKED. Carries the blocking submission when present. */
  | {
      outcome: 'CHAIN_LOCKED';
      message: string;
      submissionId?: string;
      submissionStatus?: string;
    }
  /** 400 INVALID_APPROVER — named user is not an active member of the org. */
  | { outcome: 'INVALID_APPROVER'; message: string }
  /**
   * 400 VALIDATION_ERROR whose message is the generic 'Invalid request'. The
   * server cannot tell us WHICH step failed. We must say so, not guess.
   */
  | { outcome: 'STEP_VALIDATION_UNATTRIBUTABLE'; message: string }
  /** 400 VALIDATION_ERROR that DID name a top-level field. Show it verbatim. */
  | { outcome: 'FIELD_VALIDATION'; message: string }
  | { outcome: 'UNKNOWN'; message: string };

/** The literal the backend emits when it cannot attribute a nested failure. */
export const UNATTRIBUTABLE_VALIDATION_MESSAGE = 'Invalid request';

function readCode(err: unknown): string {
  const e = err as { code?: unknown } | null;
  return e && typeof e.code === 'string' ? e.code : '';
}

function readMessage(err: unknown): string {
  const e = err as { message?: unknown } | null;
  return e && typeof e.message === 'string' ? e.message : '';
}

function readBody(err: unknown): Record<string, unknown> {
  const e = err as { response?: { data?: unknown } } | null;
  const data = e?.response?.data;
  return data && typeof data === 'object'
    ? (data as Record<string, unknown>)
    : {};
}

/**
 * Turn a rejected chain write into a state the UI can render truthfully.
 *
 * Errors arriving here have already passed through `normalizeAxiosError` in
 * lib/api.ts, so `.code` and `.message` are populated and the raw body is
 * re-attached at `.response.data`.
 */
export function classifyChainWriteError(err: unknown): ChainWriteFailure {
  const code = readCode(err);
  const message = readMessage(err);

  if (code === 'WORKSPACE_REQUIRED') {
    return {
      outcome: 'WORKSPACE_MISSING',
      message:
        'No workspace is selected. Open this gate from inside a workspace to configure its approvers.',
    };
  }

  if (code === 'FORBIDDEN_ROLE' || code === 'SUSPENDED') {
    return { outcome: 'NOT_A_MEMBER', message };
  }

  if (code === 'GATE_CHAIN_LOCKED') {
    const body = readBody(err);
    const submissionId =
      typeof body.submissionId === 'string' ? body.submissionId : undefined;
    const submissionStatus =
      typeof body.submissionStatus === 'string'
        ? body.submissionStatus
        : undefined;
    return { outcome: 'CHAIN_LOCKED', message, submissionId, submissionStatus };
  }

  if (code === 'INVALID_APPROVER') {
    return { outcome: 'INVALID_APPROVER', message };
  }

  if (code === 'VALIDATION_ERROR') {
    // The ONLY signal distinguishing an unattributable nested failure from a
    // named top-level one is the message text itself. Measured, not assumed.
    if (message === UNATTRIBUTABLE_VALIDATION_MESSAGE) {
      return { outcome: 'STEP_VALIDATION_UNATTRIBUTABLE', message };
    }
    return { outcome: 'FIELD_VALIDATION', message };
  }

  return {
    outcome: 'UNKNOWN',
    message: message || 'Could not save the approval chain.',
  };
}

// ─── Writes ─────────────────────────────────────────────────

/**
 * Create or REPLACE the chain for a gate definition.
 *
 * This is a whole-chain replace: the backend soft-deletes the current chain and
 * inserts the submitted steps. There is no per-step edit route.
 *
 * Blocked with 409 GATE_CHAIN_LOCKED while a DRAFT or SUBMITTED submission
 * exists for the gate. Configuring before any draft exists is not blocked.
 */
export async function createApprovalChain(
  input: CreateApprovalChainInput,
): Promise<ApprovalChain> {
  const resp = await request.post<unknown>(
    '/work/gate-definitions/approval-chains',
    input,
  );
  return resp as ApprovalChain;
}

/**
 * Reorder existing steps.
 *
 * NOTE: measured — `reorderSteps` does NOT call `assertNoOpenSubmission`, so
 * reordering remains possible while a submission is in flight even though
 * replacing or deleting the chain is blocked. A UI must not present reorder
 * and replace as one affordance.
 */
export async function reorderApprovalChainSteps(
  chainId: string,
  stepIds: string[],
): Promise<ApprovalChain> {
  const resp = await request.put<unknown>(
    `/work/gate-definitions/approval-chains/${chainId}/reorder`,
    { stepIds },
  );
  return resp as ApprovalChain;
}

// ─── Reads ──────────────────────────────────────────────────

/**
 * Result of reading a gate's chain.
 *
 * PHASEGATEPANEL-REJECTION-1 / #1017 frontend half (pairs with #1090):
 *   - A denial is a **rejected promise** (`AUTH_FORBIDDEN` 403). Callers must
 *     not catch it into EMPTY.
 *   - A resolved `null` is **EMPTY** — genuinely no chain configured.
 *   - `INDETERMINATE` remains only for an unexpected non-null, non-chain shape.
 *
 * ⚠️ #1090's discriminating control applies here: proving denials 403 means
 * nothing unless absence still resolves EMPTY — otherwise one conflation is
 * traded for another.
 */
export type ChainReadResult =
  | { outcome: 'CHAIN'; chain: ApprovalChain }
  | { outcome: 'EMPTY' }
  | { outcome: 'INDETERMINATE' };

function looksLikeChain(value: unknown): value is ApprovalChain {
  return (
    !!value &&
    typeof value === 'object' &&
    typeof (value as { id?: unknown }).id === 'string' &&
    Array.isArray((value as { steps?: unknown }).steps)
  );
}

export async function readChainForGate(
  gateDefinitionId: string,
): Promise<ChainReadResult> {
  // Denials reject (normalizeAxiosError → AUTH_FORBIDDEN). Do not catch here.
  const resp = await request.get<unknown>(
    `/work/gate-definitions/${gateDefinitionId}/approval-chain`,
  );
  if (looksLikeChain(resp)) {
    return { outcome: 'CHAIN', chain: resp };
  }
  if (resp == null) {
    return { outcome: 'EMPTY' };
  }
  return { outcome: 'INDETERMINATE' };
}
